import { useApi } from './api';
import { readJsonApiResponse, type ApiResponse } from './apiResponse';
import type { StateDTO, MunicipalityDTO, CityDTO, PageResponse } from '../types/address';

export const useAddressService = () => {
  const { apiFetch } = useApi();

  const getStates = async (): Promise<ApiResponse<StateDTO[]>> => {
    try {
      const res = await apiFetch('/addresses/states');
      return readJsonApiResponse(res, 'Error al obtener estados', []);
    } catch (error) {
      console.error('Error getting states:', error);
      return {
        ok: false,
        status: 500,
        data: [],
        error: error instanceof Error ? error.message : 'Error de conexión'
      };
    }
  };

  const searchCitiesByStateAndMunicipality = async (
    stateCode: string,
    municipalityCode: string,
    searchTerm: string,
    page = 0,
    size = 20
  ): Promise<ApiResponse<PageResponse<CityDTO>>> => {
    const emptyPage = {
      content: [],
      pageable: { pageNumber: 0, pageSize: size },
      totalElements: 0,
      totalPages: 0
    };
    if (searchTerm.length < 3) {
      return { ok: true, status: 200, data: emptyPage };
    }
    try {
      const res = await apiFetch(
        `/addresses/states/${stateCode}/municipalities/${municipalityCode}/cities/search?q=${encodeURIComponent(searchTerm)}&page=${page}&size=${size}`
      );
      return readJsonApiResponse(res, 'Error al buscar ciudades', emptyPage);
    } catch (error) {
      console.error('Error searching cities by state and municipality:', error);
      return {
        ok: false,
        status: 500,
        data: emptyPage,
        error: error instanceof Error ? error.message : 'Error de conexión'
      };
    }
  };

  const searchCities = async (
    searchTerm: string,
    page = 0,
    size = 20
  ): Promise<ApiResponse<PageResponse<CityDTO>>> => {
    const emptyPage = {
      content: [],
      pageable: { pageNumber: 0, pageSize: size },
      totalElements: 0,
      totalPages: 0
    };
    if (searchTerm.length < 3) {
      return { ok: true, status: 200, data: emptyPage };
    }
    try {
      const res = await apiFetch(
        `/addresses/cities/search?q=${encodeURIComponent(searchTerm)}&page=${page}&size=${size}`
      );
      return readJsonApiResponse(res, 'Error al buscar ciudades', emptyPage);
    } catch (error) {
      console.error('Error searching cities:', error);
      return {
        ok: false,
        status: 500,
        data: emptyPage,
        error: error instanceof Error ? error.message : 'Error de conexión'
      };
    }
  };

  const getMunicipalitiesByState = async (stateCode: string): Promise<ApiResponse<MunicipalityDTO[]>> => {
    try {
      const res = await apiFetch(`/addresses/states/${stateCode}/municipalities`);
      return readJsonApiResponse(res, 'Error al obtener municipios', []);
    } catch (error) {
      console.error('Error getting municipalities:', error);
      return {
        ok: false,
        status: 500,
        data: [],
        error: error instanceof Error ? error.message : 'Error de conexión'
      };
    }
  };

  const searchMunicipalitiesByState = async (
    stateCode: string,
    searchTerm: string,
    page = 0,
    size = 20
  ): Promise<ApiResponse<PageResponse<MunicipalityDTO>>> => {
    const emptyPage = {
      content: [],
      pageable: { pageNumber: 0, pageSize: size },
      totalElements: 0,
      totalPages: 0
    };
    if (searchTerm.length < 3) {
      return { ok: true, status: 200, data: emptyPage };
    }
    try {
      const res = await apiFetch(
        `/addresses/states/${stateCode}/municipalities/search?q=${encodeURIComponent(searchTerm)}&page=${page}&size=${size}`
      );
      return readJsonApiResponse(res, 'Error al buscar municipios', emptyPage);
    } catch (error) {
      console.error('Error searching municipalities:', error);
      return {
        ok: false,
        status: 500,
        data: emptyPage,
        error: error instanceof Error ? error.message : 'Error de conexión'
      };
    }
  };

  return {
    getStates,
    searchCitiesByStateAndMunicipality,
    searchCities,
    getMunicipalitiesByState,
    searchMunicipalitiesByState
  };
};
